/**
 * Verdeelt een bedrag in hele centen over percentages, met grootste-rest en
 * tekenbewust. Spiegelt App\Distribution\ShareAllocator zodat wat je in de
 * editor ziet exact overeenkomt met wat de backend straks vastlegt.
 */
export function allocateShares(amountCents: number, percentages: number[]): number[] {
  if (percentages.length === 0) return []

  const negative = amountCents < 0
  const magnitude = Math.abs(amountCents)

  const base: number[] = []
  const remainders: number[] = []
  let assigned = 0

  percentages.forEach((percentage, index) => {
    const exact = (magnitude * percentage) / 100
    const whole = Math.floor(exact)
    base[index] = whole
    remainders[index] = exact - whole
    assigned += whole
  })

  const leftover = magnitude - assigned
  const order = percentages
    .map((_, index) => index)
    .sort((a, b) => (remainders[b] - remainders[a]) || a - b)

  for (let i = 0; i < leftover; i += 1) {
    base[order[i % order.length]] += 1
  }

  return base.map((cents) => (negative ? -cents : cents))
}

export function toCents(amount: string): number {
  const parsed = Number(amount.replace(',', '.'))
  return Number.isFinite(parsed) ? Math.round(parsed * 100) : 0
}

/**
 * Verdeelt 100% gelijk over `count` ontvangers met grootste-rest, gerekend in
 * eenheden van 1/10000 procent (NUMERIC(7,4)), zodat de som altijd exact
 * 100.0000 is en niemand met de hand hoeft uit te rekenen.
 */
export function splitEvenly(count: number): string[] {
  if (count <= 0) return []

  const UNIT = 10000
  const total = 100 * UNIT
  const base = Math.floor(total / count)
  const remainder = total - base * count

  return Array.from({ length: count }, (_, index) => {
    const units = base + (index < remainder ? 1 : 0)
    return (units / UNIT).toString()
  })
}

/** Percentage-invoer als getal; accepteert zowel komma als punt. */
export function sharePercent(percentage: string): number {
  const parsed = Number(percentage.replace(',', '.'))
  return Number.isFinite(parsed) ? parsed : 0
}

/** Som van percentages, epsilon-veilig vergeleken op 4 decimalen (NUMERIC(7,4)). */
export function isCompleteShare(shares: { percentage: string }[]): boolean {
  const total = shares.reduce((sum, share) => sum + sharePercent(share.percentage), 0)
  return Math.round(total * 10000) === 1000000
}

/** Rondt een percentage-string af voor weergave, nooit voor de bewerkbare input zelf. */
export function formatSharePercent(percentage: string): string {
  return sharePercent(percentage).toLocaleString('nl-NL', { maximumFractionDigits: 2 })
}
